import { Button, Chip, DialogHandler, fetchRoute, GenericWrapperContext, getBrandUrl, Icon, LoadingContainer, Modal, SubNav, TemplateModel, TemplatesFactory, TNumStr, TTemplateModel, TTemplateShareAccounPayload, TTemplateSharedAccount, TTemplateSharePayload } from "cakemail-ui-components-v2"
import { MouseEvent, useContext, useEffect, useRef, useState, useTransition } from "react"
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom"
import { ETemplateSubNav, TTemplateDetails, TUpdateTemplate } from "./types"
import { useTranslation } from "react-i18next"
import classNames from "classnames"
import { TemplateDetailContainerContext } from "./context"
import { TButton, TButtonRef } from "cakemail-ui-components-v2/dist/esm/components/button/types"
import { ERoutes } from "../../utils/url"
import { beeTemplate } from "../../data/bee"
import { TemplatePreview } from "../../components/templates/preview"
import { TemplateMainDetails } from "../../components/templates/mainDetails"
import Sharing from "../../components/templates/sharing"


export default function TemplateDetails({ currentTemplate, isNewAccount }: TTemplateDetails) {
    const genericWrapperContext = useContext(GenericWrapperContext)
    const navigate = useNavigate()

    const params = useParams()
    const pathLocation = useLocation()

    const { t, i18n } = useTranslation()

    const defaultName = useRef(t("general-name-untitled"))

    const [showPreview, setShowPreview] = useState<boolean>(false)
    const [template, setTemplate] = useState<TemplateModel>()
    const templateRef = useRef<TemplateModel>(new TemplateModel({}))

    const isNew = isNewAccount || params.templateId === "new"
    const isPublished = template?.sharing.shared

    const pathParts = pathLocation.pathname.split("/")
    let nav = pathParts[pathParts.length - 1]

    if (nav === "details") {
        nav = ERoutes.templateDetails
    } else if (nav === "edits") {
        nav = ERoutes.templateEdit
    } else if (nav === "sharing") {
        nav = ERoutes.templateSharing
    }

    useEffect(getTemplate, [])

    function getTemplate(fresh: boolean = false) {
        if (isNew && !fresh) {
            setTemplate(new TemplateModel({}))
            templateRef.current = new TemplateModel({})
        } else {
            fetchTemplate()
        }
    }

    async function fetchTemplate({ btn }: { btn?: TButtonRef } = {}) {
        return TemplatesFactory.get({ id: String(params.templateId) }).then((a) => {
            setTemplate(new TemplateModel(a))
            templateRef.current = a
            btn && btn.reset()
        }).catch(({ msg }) => {
            navigate(fetchRoute({ route: ERoutes.assets }))
            DialogHandler.show({
                title: t(msg || "general-error-msg")
            })
        })
    }


    function handleChangeSubNav(event: MouseEvent<HTMLButtonElement>) {
        const { name } = event.currentTarget
        navigate(fetchRoute({ route: name as ERoutes, params: { templateId: template?.id } }))
    }

    function handlePreview() {
        setShowPreview(!showPreview)
    }

    function updateTemplate({ data, btnRef, goToEdit, noConfirmation }: TUpdateTemplate): Promise<void> | void {
        if (!template) return

        if (isNew) {

            return TemplatesFactory.create({
                template: {
                    name: template.name || defaultName.current,
                    description: template.description || undefined,
                    tags: ["free", "sys_lang_en_us", ...template.tags, "bee"],
                    //@ts-ignore
                    content: {
                        type: "bee",
                        json: beeTemplate()
                    }
                }
            })
                .then((updatedTemplate) => {
                    if (goToEdit) {
                        handleEdit(updatedTemplate.id)
                        return
                    }
                    navigate(fetchRoute({ route: ERoutes.templateDetails, params: { templateId: updatedTemplate.id } }))
                    setTemplate(new TemplateModel(updatedTemplate))
                    templateRef.current = new TemplateModel(updatedTemplate)
                    btnRef && btnRef.reset()
                    if (!noConfirmation) {
                        DialogHandler.show({
                            type: "success",
                            title: t("template-detail-updated-txt")
                        })
                    }
                    return
                })
        }

        return template.update(data || {})
            .then((updatedTemplate) => {
                setTemplate(new TemplateModel(updatedTemplate.data))
                templateRef.current = new TemplateModel(updatedTemplate.data)
                btnRef && btnRef.reset()
                if (!noConfirmation) {
                    DialogHandler.show({
                        type: "success",
                        title: t("template-detail-updated-txt")
                    })
                }
                return
            }).catch(({ msg }) => {
                btnRef && btnRef.reset()
                DialogHandler.show({
                    type: "warning",
                    title: t(msg || "template-detail-updated-error")
                })
            })
    }

    function handleEdit(templateId?: TNumStr) {
        window.location.href = "#" + fetchRoute({ route: ERoutes.templateEdit, params: { templateId: templateId || params?.templateId } })
    }

    async function handleShare({ accounts, automatic, everyone, btn }: TTemplateSharePayload & { btn?: TButtonRef } = {}) {
        return template?.share({ automatic, everyone, accounts }).then(() => {
            return fetchTemplate({ btn })
        }).catch(({ msg, error }) => {
            DialogHandler.show({
                type: "warning",
                title: t(msg || "template-share-error")
            })
            btn && btn.reset()
            throw error
        })
    }

    async function handleUnshare({ btn }: { btn?: TButtonRef }) {
        return template?.unshare().then(() => {
            return fetchTemplate({ btn })
        }).catch(({ msg, error }) => {
            DialogHandler.show({
                type: "warning",
                title: t(msg || "template-unshare-error")
            })
            btn && btn.reset()
            throw error
        })
    }

    let isLoading = false
    let isOwner = template?.is_owner
    if (!template) {
        isLoading = true
    }

    const modalClasses = classNames("assetManagerModal", "templateModal", {
        "isPreview": showPreview
    })

    return <Modal
        open
        className={modalClasses}
        heading={{
            logoUrl: getBrandUrl({
                elKey: "logo",
                brand: genericWrapperContext?.partnerBrand,
                locale: genericWrapperContext?.user?.language
            }),
            titleContent: [<SubNav
                key="subNav"
                menuVariant="text"
                onClick={handleChangeSubNav}
                initialSelected={nav}
                items={[
                    { name: ERoutes.templateDetails, text: t("assets-sub-nav-details") },
                    {
                        name: ERoutes.templateEdit, text: t(isNew ? "assets-sub-nav-create-template" : "assets-sub-nav-edit"), onClick: () => {
                            if (isNew) {
                                updateTemplate({ goToEdit: true })
                            } else {
                                handleEdit()
                            }
                        },
                        disabled: !isOwner && !isNew
                    },
                    { name: ERoutes.templateSharing, text: t("assets-sub-nav-sharing"), disabled: isNew },
                ]}
            />],
            actions: [
                <Button
                    key="button1"
                    disabled={!template}
                    onClick={handlePreview}
                    startIcon={<Icon name={showPreview ? "X18" : "Preview18"} />}
                    variant="outlined"
                    color="secondary"
                >
                    {t("general-preview-BTN")}
                </Button>,

                (
                    isPublished ?
                        <Button
                            key="button3"
                            disabled={!template || !isOwner}
                            animated
                            onClick={(e, btn) => { handleUnshare({ btn }) }}
                            startIcon={<Icon name={"Publish18"} />}
                            variant="contained"
                            color={!isOwner ? "cloud" : "primary"}
                        >
                            {t(!isOwner ? "general-shared" : "general-unpublish")}
                        </Button>
                        :
                        <Button
                            key="button2"
                            animated
                            disabled={!template || isNew || !isOwner}
                            onClick={(e, btn) => { handleShare({ btn }) }}
                            startIcon={<Icon name={"Publish18"} />}
                            variant="contained"
                            color={!isOwner ? "cloud" : "primary"}
                        >
                            {t(!isOwner ? "general-unshared" : "general-publish")}
                        </Button>
                )
            ],
            closeBtnOnClick: () => { navigate(fetchRoute({ route: ERoutes.assets })) },
            closeBtnText: t("general-close")
        }}
        content={
            <LoadingContainer loading={isLoading}>
                {template && templateRef &&
                    <TemplateDetailContainerContext.Provider value={{
                        template,
                        templateRef,
                        setTemplate,
                        updateTemplate,
                        handleShare,
                        handleUnshare
                    }}>
                        {(showPreview) && <TemplatePreview />}
                        {(!showPreview) && <Routes>
                            <Route
                                path={"/"}
                                element={<TemplateMainDetails isNew={isNew} />}
                            />
                            <Route
                                path={fetchRoute({ route: ERoutes.templateSharing })}
                                element={<Sharing />}
                            />

                            <Route
                                path={'sharing'}
                                element={<Sharing />}
                            />

                        </Routes>}
                    </TemplateDetailContainerContext.Provider>
                }

            </LoadingContainer>
        }
    />

}
